import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api';
import { Message } from '../message';
import { MessageType } from '../../types/message';
import styles from './ChatHistory.module.css';

// Simple interfaces that match what the component actually needs
interface Character {
  id: string;
  chat_history: () => Promise<ChatHistoryItem[]>;
}

interface ChatHistoryItem {
  id: string;
  time_updated: string;
  messages: MessageType[];
}

const formatTimeAgo = (date: string): string => {
  const seconds = Math.floor((new Date().getTime() - new Date(date).getTime()) / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (seconds < 60) return 'recently';
  if (minutes < 60) return `${minutes} minutes ago`;
  if (hours < 2) return 'about an hour ago';
  if (hours < 24) return `${hours} hours ago`;
  if (days < 30) return `${days} days ago`;
  return new Date(date).toLocaleDateString();
};

interface ChatHistoryProps {
  character: Character;
}

const ChatHistory: React.FC<ChatHistoryProps> = ({ character }) => {
  const navigate = useNavigate();
  const [chatHistory, setChatHistory] = useState<ChatHistoryItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchChatHistory = async (): Promise<void> => {
      try {
        const history = await character.chat_history();
        setChatHistory(history);
      } catch (err) {
        console.error('Failed to fetch chat history:', err);
        setError('Failed to load chat history');
      } finally {
        setLoading(false);
      }
    };

    fetchChatHistory();
  }, [character]);

  const handleNewChat = async (): Promise<void> => {
    try {
      const chat = await api.chats.create(character.id);
      navigate(`/chats/${chat.id}`);
    } catch (err) {
      console.error('Failed to create new chat:', err);
      setError('Failed to create new chat');
    }
  };

  if (loading) return <div className={styles.loading}>Loading chat history...</div>;
  if (error) return <div className={styles.error}>{error}</div>;

  return (
    <div className={styles.container}>
      {!chatHistory.length ? (
        <div className={styles.empty}>
          <p>No chat history yet</p>
        </div>
      ) : (
        chatHistory.map((chat) => (
          <div key={chat.id}>
            <div className={styles.timestampDivider}>
              <span className={styles.timestamp}>{formatTimeAgo(chat.time_updated)}</span>
            </div>
            <div 
              className={styles.chatPreview}
              onClick={() => navigate(`/chats/${chat.id}`)}
            >
              <div className={styles.messages}>
                {chat.messages.map((message) => (
                  <Message key={message.id} message={message} />
                ))}
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default ChatHistory;